import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Plus, Clock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MeusLivros() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/books`, {
        withCredentials: true,
      });
      setBooks(response.data);
    } catch (error) {
      console.error('Error fetching books:', error);
      toast.error('Erro ao carregar livros');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      em_andamento: { text: 'Em Andamento', class: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
      finalizado: { text: 'Finalizado', class: 'bg-green-500/10 text-green-600 dark:text-green-400' },
      publicado: { text: 'Publicado', class: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
    };
    const badge = badges[status] || badges.em_andamento;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.class}`}>
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="flex-1 md:ml-64 p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-64 bg-muted rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-16 md:pt-8" data-testid="meus-livros-page">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl md:text-5xl font-light mb-2">Meus Livros</h1>
              <p className="text-muted-foreground">{books.length} {books.length === 1 ? 'livro' : 'livros'} no seu acervo</p>
            </div>
            <Button
              onClick={() => navigate('/novo-livro')}
              size="lg"
              className="bg-amber-500 hover:bg-amber-600 text-stone-900"
              data-testid="new-book-button"
            >
              <Plus className="mr-2" size={20} />
              Novo Livro
            </Button>
          </div>

          {books.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-border rounded-lg">
              <BookOpen className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-medium mb-2">Nenhum livro ainda</h3>
              <p className="text-muted-foreground mb-6">Comece sua jornada literária criando seu primeiro livro</p>
              <Button
                onClick={() => navigate('/novo-livro')}
                className="bg-amber-500 hover:bg-amber-600 text-stone-900"
              >
                Criar Primeiro Livro
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {books.map((book) => (
                <div
                  key={book.book_id}
                  className="bg-card border border-border/40 rounded-lg overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                  onClick={() => navigate(`/livro/${book.book_id}`)}
                  data-testid={`book-card-${book.book_id}`}
                >
                  {/* Clean 3D Book Cover */}
                  <div className="h-64 bg-gradient-to-br from-stone-100 to-stone-200 dark:from-stone-800 dark:to-stone-900 flex items-center justify-center py-6">
                    <div style={{ perspective: '800px' }}>
                      <div 
                        className="relative transition-transform duration-500 group-hover:scale-105"
                        style={{ 
                          transformStyle: 'preserve-3d',
                          transform: 'rotateY(-12deg)',
                          width: '130px',
                          height: '190px'
                        }}
                      >
                        {/* Spine */}
                        <div 
                          style={{
                            position: 'absolute',
                            top: '0',
                            left: '0',
                            width: '14px',
                            height: '100%',
                            background: 'linear-gradient(to right, #2a3a4a, #3a4a5a, #2a3a4a)',
                            transform: 'rotateY(90deg) translateX(-7px)',
                            transformOrigin: 'left center',
                            borderRadius: '2px 0 0 2px'
                          }}
                        />
                        {/* Pages */}
                        <div 
                          style={{
                            position: 'absolute',
                            top: '2px',
                            right: '-6px',
                            width: '12px',
                            height: 'calc(100% - 4px)',
                            background: 'linear-gradient(to right, #f8f8f5, #fff)',
                            borderRadius: '0 1px 1px 0'
                          }}
                        />
                        {/* Front */}
                        <div 
                          style={{ 
                            position: 'absolute',
                            top: '0',
                            left: '0',
                            width: '100%',
                            height: '100%',
                            borderRadius: '0 3px 3px 0',
                            overflow: 'hidden',
                            boxShadow: '4px 4px 15px rgba(0,0,0,0.15)'
                          }}
                        >
                          {book.cover_url ? (
                            <img src={book.cover_url} alt={book.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center">
                              <BookOpen className="w-8 h-8 text-stone-300" />
                            </div>
                          )}
                          <div style={{ position: 'absolute', inset: '0', pointerEvents: 'none', background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)' }} />
                        </div>
                        {/* Shadow */}
                        <div style={{
                          position: 'absolute',
                          bottom: '-10px',
                          left: '10%',
                          width: '80%',
                          height: '10px',
                          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.12) 0%, transparent 70%)',
                          filter: 'blur(3px)'
                        }} />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-serif font-medium line-clamp-2 flex-1">{book.title}</h3>
                      {/* Coauthor badge */}
                      {book.is_collaboration && (
                        <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center gap-1 shrink-0">
                          <Users size={12} />
                          Coautor
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{book.author}</p>
                    
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Capítulos</span>
                        <span className="font-medium">{book.chapters_count}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Palavras</span>
                        <span className="font-medium">{book.total_words.toLocaleString('pt-BR')}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      {getStatusBadge(book.status)}
                      <div className="flex items-center text-xs text-muted-foreground">
                        <Clock size={12} className="mr-1" />
                        {new Date(book.updated_at).toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
